/**
 * @fileoverview Date utility functions for the Budget Tracker application.
 *
 * Provides timezone-safe date manipulation functions for:
 * - Getting current dates in local timezone
 * - Calculating budget months (first day of month)
 * - Formatting dates for display
 * - Month navigation (previous/next)
 * - Date comparisons and validation
 *
 * @description All functions handle timezone issues carefully to ensure dates
 * are interpreted in the user's local timezone, not UTC. This is critical for
 * budget tracking where month boundaries must align with local time.
 *
 * Key concepts:
 * - Budget month: Always the 1st day of a month (YYYY-MM-01 format)
 * - Local timezone: All dates use local time, not UTC
 * - Date strings: YYYY-MM-DD format for consistency with backend
 *
 * @module utils/dateUtils
 */

/**
 * Get today's date in YYYY-MM-DD format (timezone safe).
 * Uses local timezone offset to avoid UTC issues.
 *
 * @returns {string} Today's date in YYYY-MM-DD format
 * @example
 * getTodaysDate(); // "2025-10-26"
 */
export const getTodaysDate = () => {
    const today = new Date();
    // Use local timezone offset to get the correct local date
    const localDate = new Date(today.getTime() - (today.getTimezoneOffset() * 60000));
    return localDate.toISOString().split('T')[0];
};

/**
 * Calculate budget month from a date (returns first day of month).
 * Handles timezone issues by parsing as local date.
 *
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Budget month in YYYY-MM-01 format
 * @example
 * getBudgetMonth('2025-10-26'); // "2025-10-01"
 */
export const getBudgetMonth = (dateString) => {
    // Handle timezone issues by parsing as local date
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day); // month - 1 because Date() months are 0-indexed
    const correctedMonth = String(date.getMonth() + 1).padStart(2, '0');
    const correctedYear = date.getFullYear();
    return `${correctedYear}-${correctedMonth}-01`;
};

/**
 * Get month display name from a Date object
 */
export const getMonthDisplayName = (dateObj) => {
    return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

/**
 * Check if a transaction belongs to the selected month
 */
export const isTransactionInSelectedMonth = (transaction, selectedMonth) => {
    // Parse date string directly to avoid timezone issues
    const [transYear, transMonth] = transaction.date.split('-');
    const transactionYear = parseInt(transYear);
    const transactionMonth = parseInt(transMonth) - 1; // Convert to 0-based month for comparison

    return transactionYear === selectedMonth.getFullYear() &&
        transactionMonth === selectedMonth.getMonth();
};

/**
 * Check if we're viewing the current month
 */
export const isCurrentMonth = (selectedMonth) => {
    const today = new Date();
    return selectedMonth.getFullYear() === today.getFullYear() &&
        selectedMonth.getMonth() === today.getMonth();
};

/**
 * Navigate to a different month
 */
export const navigateMonth = (currentMonth, direction) => {
    const newDate = new Date(currentMonth);
    if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
    } else if (direction === 'next') {
        newDate.setMonth(newDate.getMonth() + 1);
    }
    return newDate;
};

/**
 * Get the current month as a Date object (first day of month)
 */
export const getCurrentMonth = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
};

/**
 * Normalize date to first day of month (timezone safe)
 * Handles both YYYY-MM-DD strings and ISO date strings
 */
export const getFirstDayOfMonth = (date) => {
    if (!date) return null;

    // Extract YYYY-MM from the date string (works for both formats)
    const dateStr = typeof date === 'string' ? date : date.toISOString();
    const [year, month] = dateStr.split('-');

    return `${year}-${month}-01`;
};

/**
 * Normalize date to last day of month
 */
export const getLastDayOfMonth = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth();
    // Get last day by going to first of next month and subtracting 1 day
    const lastDay = new Date(year, month + 1, 0).getDate();
    const monthStr = String(month + 1).padStart(2, '0');
    return `${year}-${monthStr}-${lastDay}`;
};

/**
 * Format a date string from database for display (handles timezone issues)
 * Database dates are in YYYY-MM-DD format and should be displayed in local timezone
 */
export const formatDateForDisplay = (dateString) => {
    if (!dateString) return '-';
    // Split the date string and create date with local timezone
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
};